import { useQuery } from '@tanstack/react-query';
import { Smartphone, Sparkles } from 'lucide-react';

import { apiGet } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { Button } from '@/components/ui/Button';
import { Card, SectionHeader } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pill } from '@/components/ui/Pill';
import { ErrorState, LoadingState } from '@/components/ui/States';
import { fmtDate } from '@/utils/format';

interface Subscription {
  plan: string;
  cycle?: string;
  expires_at?: string;
  auto_renew?: boolean;
  payment_status?: string;
  platform?: 'android' | 'ios' | null;
}

/**
 * Subscription status — read-only on web. Apple and Google forbid
 * processing in-app subscriptions outside their billing systems for
 * mobile-app subs, so we steer the user to manage them on the device
 * where they bought the plan.
 */
export function SubscriptionPage() {
  const query = useQuery({
    queryKey: ['subscription'],
    queryFn: () => apiGet<Subscription>(endpoints.subscription),
  });

  if (query.isLoading) return <LoadingState />;
  if (query.isError) {
    return (
      <ErrorState
        title="Couldn't load subscription"
        description={(query.error as Error)?.message}
        onRetry={() => query.refetch()}
      />
    );
  }

  const s = query.data ?? { plan: 'trial' };
  const isTrial = s.plan === 'trial';
  const tone = isTrial ? 'warn' : 'success';
  const cycleLabel = s.cycle === 'annual' ? 'yearly' : s.cycle ?? '';

  return (
    <>
      <PageHeader
        title="Subscription"
        description="Your current New Vishwas Electronics plan."
      />

      <Card className="mb-4">
        <div className="flex items-start gap-4 flex-wrap">
          <div
            className={`h-16 w-16 rounded-2xl grid place-items-center ${
              isTrial ? 'bg-warn/10 text-warn' : 'bg-success/10 text-success'
            }`}
          >
            <Sparkles className="h-7 w-7" />
          </div>
          <div className="flex-1 min-w-0">
            <Pill tone={tone}>
              {s.payment_status ?? (isTrial ? 'trial' : 'active')}
            </Pill>
            <h2 className="text-2xl font-bold mt-2 capitalize">
              {isTrial
                ? 'Trial'
                : `Pro${cycleLabel ? ` · ${cycleLabel}` : ''}`}
            </h2>
            {s.expires_at && (
              <p className="text-sm text-ink-soft mt-1">
                {isTrial ? 'Trial ends ' : 'Renews '}
                {fmtDate(s.expires_at)}
                {s.auto_renew && !isTrial && ' (auto-renew on)'}
              </p>
            )}
          </div>
        </div>
      </Card>

      <Card className="mb-4">
        <SectionHeader title="Manage your plan" />
        <p className="text-sm text-ink-soft mb-4">
          Subscriptions are processed by your phone's app store. Open the
          New Vishwas Electronics app on your phone to upgrade, downgrade, or cancel.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            iconLeft={<Smartphone className="h-4 w-4" />}
            onClick={() =>
              window.open(
                'https://play.google.com/store/account/subscriptions',
                '_blank',
                'noopener',
              )
            }
          >
            Manage on Play Store
          </Button>
          <Button
            variant="outline"
            iconLeft={<Smartphone className="h-4 w-4" />}
            onClick={() =>
              window.open(
                'https://apps.apple.com/account/subscriptions',
                '_blank',
                'noopener',
              )
            }
          >
            Manage on App Store
          </Button>
        </div>
      </Card>

      <Card>
        <SectionHeader title="Why no upgrade button here?" />
        <p className="text-sm text-ink-soft">
          Apple and Google require that subscriptions for their mobile apps
          run through their billing systems — we can't take a card directly
          on this panel. The trade-off: cancel any time from the same place,
          taxes are handled, family sharing works.
        </p>
      </Card>
    </>
  );
}
